import { JSX, useRef } from "react";

import {
  CriticalIllnessAgeTiers,
  CriticalIllnessRate,
} from "../../constants/aquafina";
import {
  CoverageArrowRow,
  CoverageHeaderRow,
  CoverageRow,
  MessageRow,
} from "../table/rows";
import { RangeIterator } from "../../utils";
import {
  CoverageCell,
  CoverageHeaderCell,
  CoverageMultiVectorCell,
  CoverageMultiVectorHeaderCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  INCONSISTENT_DATA,
} from "../table/cells";

type Field = "employee" | "spouse";

type TierPlan = Record<Field, string[]>;
const NON_TOBACCO_INDEX = 0;
const TOBACCO_INDEX = 1;

const isAgeTiersValid = (ageTiers: CriticalIllnessAgeTiers[]): boolean =>
  ageTiers.every((at) => at.isTobacco !== null) ||
  ageTiers.every((at) => at.isTobacco === null);

const setInitialAgeTier = (
  isTobaccoNull: boolean,
  includeSpouse: boolean,
): TierPlan => {
  const defaultTobaccoValue: string[] = isTobaccoNull ? [] : ["", ""];
  const result: TierPlan = {
    employee: defaultTobaccoValue,
    spouse: includeSpouse ? defaultTobaccoValue : [],
  };
  return result;
};

const CriticalIllnessRates = ({
  rates,
}: {
  rates: CriticalIllnessRate[];
}): JSX.Element => {
  const tiers = new Map<string, TierPlan[]>();
  const hasInconsistency = useRef(false);

  const updateTier = (
    ageTiers: CriticalIllnessAgeTiers[],
    isTobaccoNull: boolean,
    field: Field,
    index: number,
    hasSpouse: boolean,
  ): void => {
    ageTiers.forEach((tier) => {
      const { age, rate: tierRate, isTobacco } = tier;

      if (!tiers.has(age)) {
        tiers.set(age, []);
      }

      if (!tiers.get(age)![index]) {
        tiers.get(age)![index] = setInitialAgeTier(isTobaccoNull, hasSpouse);
      }

      const entry = tiers.get(age)![index];
      if (isTobaccoNull) {
        entry[field] = [tierRate];
      } else {
        entry[field][isTobacco ? TOBACCO_INDEX : NON_TOBACCO_INDEX] = tierRate;
      }
    });
  };

  RangeIterator(rates.length, (index) => {
    if (hasInconsistency.current) {
      return;
    }

    const rate = rates[index];
    const eeAgeTiers = rate.employee.ageTiers;
    const hasSpouse = rate.spouse !== null;
    const spAgeTiers = rate.spouse?.ageTiers || [];

    if (!isAgeTiersValid(eeAgeTiers)) {
      hasInconsistency.current = true;
      return;
    }

    if (hasSpouse && !isAgeTiersValid(spAgeTiers)) {
      hasInconsistency.current = true;
      return;
    }

    const eeTobaccoNull = eeAgeTiers.every((at) => at.isTobacco === null);
    updateTier(eeAgeTiers, eeTobaccoNull, "employee", index, hasSpouse);

    if (hasSpouse) {
      const spTobaccoNull = spAgeTiers.every((at) => at.isTobacco === null);
      updateTier(spAgeTiers, spTobaccoNull, "spouse", index, true);
    }
  });

  if (hasInconsistency.current) {
    return (
      <MessageRow
        colSpan={rates.length}
        message="This section cannot be displayed due to inconsistent data"
      />
    );
  }

  const ratesArray = Array.from(tiers.entries());

  const getMultiVectorHeader = (index: number, field: Field): string[] => {
    const { 1: values } = ratesArray[0];
    return values[index][field].length > 1 ? ["Non-Tobacco", "Tobacco"] : [""];
  };

  const hasSpouseColumn = (index: number): boolean =>
    rates[index].spouse !== null;

  const buildChildRate = (index: number): string | null => {
    const rate = rates[index];
    if (!rate.child || !rate.childRateUnit) {
      return null;
    }
    if (
      rate.child === INCONSISTENT_DATA ||
      rate.childRateUnit === INCONSISTENT_DATA
    ) {
      return INCONSISTENT_DATA;
    }

    if (rate.childRateUnit === "usd") {
      return rate.child;
    }

    return `${rate.child} ${rate.childRateUnit}`;
  };

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(rates.length, (index) =>
          hasSpouseColumn(index) ? (
            <CoverageVectorHeaderCell
              label={["Employee", "Spouse"]}
              key={index}
            />
          ) : (
            <CoverageHeaderCell label="Employee" key={index} />
          ),
        )}
      </CoverageHeaderRow>
      <CoverageRow label="Rate Unit">
        {RangeIterator(rates.length, (index) =>
          hasSpouseColumn(index) ? (
            <CoverageVectorCell
              value={[
                rates[index].employee.rateUnit,
                rates[index].spouse?.rateUnit ?? null,
              ]}
              key={index}
            />
          ) : (
            <CoverageCell value={rates[index].employee.rateUnit} key={index} />
          ),
        )}
      </CoverageRow>
      {ratesArray.length > 0 ? (
        <>
          <CoverageRow label="Rates" isWhite>
            {RangeIterator(rates.length, (index) =>
              hasSpouseColumn(index) ? (
                <CoverageMultiVectorHeaderCell
                  labels={[
                    getMultiVectorHeader(index, "employee"),
                    getMultiVectorHeader(index, "spouse"),
                  ]}
                  key={index}
                  fontWeight="normal"
                />
              ) : (
                <CoverageVectorHeaderCell
                  label={getMultiVectorHeader(index, "employee")}
                  key={index}
                />
              ),
            )}
          </CoverageRow>
          {ratesArray.map(([label, values], indexTier) => (
            <CoverageArrowRow
              key={label}
              label={label}
              level={2}
              isWhite={indexTier % 2 !== 0}
            >
              {/* eslint-disable react/no-array-index-key */}
              {values.map((value, index) =>
                hasSpouseColumn(index) ? (
                  <CoverageMultiVectorCell
                    value={[value.employee, value.spouse]}
                    key={index}
                  />
                ) : (
                  <CoverageVectorCell value={value.employee} key={index} />
                ),
              )}
              {/* eslint-enable react/no-array-index-key */}
            </CoverageArrowRow>
          ))}
        </>
      ) : null}
      <CoverageRow
        label="Child Rate"
        isWhite={ratesArray.length === 0 ? true : ratesArray.length % 2 !== 0}
      >
        {RangeIterator(rates.length, (index) => (
          <CoverageCell value={buildChildRate(index)} key={index} />
        ))}
      </CoverageRow>
    </>
  );
};

export default CriticalIllnessRates;
